import React, { useEffect, useState } from 'react';
import {
    HashRouter as Router,
    Switch,
    Route,
    Redirect,
} from 'react-router-dom';

// 用 HashRouter 而非 BrowserRouter, 原因:
// - 静态资源部署到 OSS 子路径 (/material-frontend/<mode>/), 用 # 作为 client 路由分隔
//   URL 形如 https://.../material-frontend/dev/#/materials/AB001-01
//   服务器只看到 /material-frontend/dev/index.html, # 之后的部分由 React 接管
// - OSS 不需要额外配 "静态网站托管 404 → index.html", 刷新永远 OK
// - 缺点是 URL 里带 #, 不利于 SEO; 对内部工具系统不是大事
import { Provider } from 'react-redux';
import store, { actions } from './store';
import Sidebar from './components/layout/Sidebar';
import TopBar from './components/layout/TopBar';

// material
import MaterialList from './pages/material/MaterialList';
import MaterialDetail from './pages/material/MaterialDetail';
import MaterialCreateWizard from './pages/material/create/MaterialCreateWizard';
import SkuList from './pages/material/SkuList';
import TipLibrary from './pages/material/TipLibrary';
// device
import DeviceList from './pages/device/DeviceList';
import DeviceManage from './pages/device/DeviceManage';
import DeviceCreateWizard from './pages/device/create/DeviceCreateWizard';
import DeviceCalibration from './pages/device/DeviceCalibration';
import DeviceYaml from './pages/device/DeviceYaml';
// parameter
import ParamSetList from './pages/parameter/ParamSetList';
import ParamManage from './pages/parameter/ParamManage';
import ApprovalList from './pages/parameter/ApprovalList';
import MyPermissions from './pages/permissions/MyPermissions';
// 数据资产
import UgcShareList from './pages/assets/UgcShareList';
import UgcShareDetail from './pages/assets/UgcShareDetail';
import RecognitionList from './pages/assets/RecognitionList';
import RecognitionDetail from './pages/assets/RecognitionDetail';
import MatrixRecognitionList from './pages/assets/MatrixRecognitionList';
import MatrixRecognitionDetail from './pages/assets/MatrixRecognitionDetail';
import ProcessList from './pages/assets/ProcessList';
import ProcessHistoryEntry from './pages/assets/ProcessHistoryEntry';
import ProcessRecordEntry from './pages/assets/ProcessRecordEntry';
import MaterialSatisfaction from './pages/assets/MaterialSatisfaction';
import ProcessHistoryDetail from './pages/assets/ProcessHistoryDetail';
import ProductList from './pages/assets/ProductList';
import ProductDetail from './pages/assets/ProductDetail';
import ProductLabelReview from './pages/assets/ProductLabelReview';
import ProductMaterialReview from './pages/assets/ProductMaterialReview';
import C10sList from './pages/assets/C10sList';
import C10sDetail from './pages/assets/C10sDetail';
// gallery / toolbox
import SampleList from './pages/gallery/SampleList';
import MatrixList from './pages/gallery/MatrixList';
import SampleDetail from './pages/gallery/SampleDetail';
import ToolList from './pages/toolbox/ToolList';
import ToolDetail from './pages/toolbox/ToolDetail';
// governance
import IDMappingList from './pages/governance/IDMappingList';
import CompatList from './pages/governance/CompatList';
// admin
import UserManage from './pages/adminuser/UserManage';
import AssistantFeedback from './pages/admin/AssistantFeedback';
import DataDictAdmin from './pages/admin/DataDictAdmin';
import SchemaAdminRedirect from './pages/admin/SchemaAdminRedirect';
import HomeRedirect from './components/HomeRedirect';
import RouteGuard from './components/RouteGuard';
import SchemaFieldAdmin from './pages/admin/SchemaFieldAdmin';
import ParamTemplateAdmin from './pages/parameter/ParamTemplateAdmin';
import MaterialTemplateAdmin from './pages/material/MaterialTemplateAdmin';
import SkuTemplateAdmin from './pages/material/SkuTemplateAdmin';
import DeviceTemplateAdmin from './pages/device/DeviceTemplateAdmin';
import TokenList from './pages/apitoken/TokenList';
import SyncOverview from './pages/datasync/SyncOverview';
import SyncLogList from './pages/datasync/SyncLogList';
// 平台文档 (2026-10-08): 按需加载, sdkApi.json 体积大, 不进主包.
const PlatformIntro = React.lazy(() => import('./pages/docs/PlatformIntro'));
const SdkReference = React.lazy(() => import('./pages/docs/SdkReference'));
const AppApiReference = React.lazy(() => import('./pages/docs/AppApiReference'));
const docPage = (el: React.ReactNode) => <React.Suspense fallback={<div className="p-6 text-sm text-slate-400">加载中…</div>}>{el}</React.Suspense>;

import PermissionRoute from './components/PermissionRoute';
import AccessGate from './components/AccessGate';
import DialogHost from './components/DialogHost';

import { auth, UserInfo } from './utils/auth';

type AuthState =
    | { kind: 'checking' }
    | { kind: 'redirecting' }
    | { kind: 'ready'; user: UserInfo }
    | { kind: 'error'; message: string };

function App() {
    const [state, setState] = useState<AuthState>({ kind: 'checking' });

    useEffect(() => {
        if (!auth.isLoggedIn()) {
            setState({ kind: 'redirecting' });
            auth.goToLogin();
            return;
        }
        let cancelled = false;
        auth.getUserInfo()
            .then((user) => {
                if (!cancelled) {
                    setState({ kind: 'ready', user });
                    (store.dispatch as any)(actions.me.load());
                    // RBAC 生效规则与 me 同期拉取: 菜单/按钮可见性靠它 (2026-08-31).
                    (store.dispatch as any)(actions.authz.load());
                }
            })
            .catch((err) => {
                if (!cancelled) {
                    setState({ kind: 'redirecting' });
                    auth.goToLogin();
                }
                console.error('getUserInfo failed', err);
            });
        return () => {
            cancelled = true;
        };
    }, []);

    if (state.kind === 'checking') {
        return (
            <div className="min-h-screen flex items-center justify-center text-sm text-slate-500">
                正在校验登录态…
            </div>
        );
    }
    if (state.kind === 'redirecting') {
        return (
            <div className="min-h-screen flex items-center justify-center text-sm text-slate-500">
                正在跳转飞书登录…
            </div>
        );
    }
    if (state.kind === 'error') {
        return (
            <div className="min-h-screen flex items-center justify-center text-sm text-rose-600">
                登录异常: {state.message}
            </div>
        );
    }

    return (
        <Router>
            <Provider store={store}>
                <DialogHost />
                {/* 访问闸门: 非管理员未审批通过时整站替换为申请页 (2026-08-25) */}
                <AccessGate>
                    <div className="flex min-h-screen">
                        <Sidebar />
                        <main className="flex-1 flex flex-col min-w-0">
                            <TopBar user={state.user} />
                            <section className="flex-1 overflow-auto p-6">
                                <RouteGuard>
                                    <Switch>
                                        <Route exact path="/">
                                            <HomeRedirect />
                                        </Route>

                                        {/* material */}
                                        <Route exact path="/materials">
                                            <MaterialList />
                                        </Route>
                                        {/* /materials/id-mapping、/materials/categories 必须比 /:xcode 先注册, 否则会被匹配成 detail */}
                                        <Route
                                            exact
                                            path="/materials/id-mapping"
                                        >
                                            <IDMappingList />
                                        </Route>
                                        {/* 2026-08-31: 材料类别/工艺类型/字典/加工模式并入「数据字典」页 (tab 切换), 旧地址重定向兼容书签 */}
                                        <Route
                                            exact
                                            path="/materials/categories"
                                        >
                                            <Redirect to="/admin/data-dict?d=category" />
                                        </Route>
                                        {/* 材料模板 (2026-09-12 从 Schema 管理拆出); 必须先于 /:xcode 注册 */}
                                        <Route
                                            exact
                                            path="/materials/templates"
                                        >
                                            <PermissionRoute
                                                resource="phys_schema"
                                                verb="update"
                                            >
                                                <MaterialTemplateAdmin />
                                            </PermissionRoute>
                                        </Route>
                                        {/* 材料创建向导 (2026-09-16): 「新建材料」的唯一入口, 必须先于 /:xcode 注册 */}
                                        <Route exact path="/materials/new">
                                            <PermissionRoute
                                                resource="material_bundle"
                                                verb="create"
                                            >
                                                <MaterialCreateWizard />
                                            </PermissionRoute>
                                        </Route>
                                        <Route exact path="/materials/:xcode">
                                            <MaterialDetail />
                                        </Route>
                                        {/* SKU 库 (以 SKU 为主键的平铺视图) */}
                                        <Route exact path="/skus">
                                            <SkuList />
                                        </Route>
                                        {/* SKU 规格模板 (2026-10-14) */}
                                        <Route exact path="/skus/templates">
                                            <PermissionRoute
                                                resource="sku_schema"
                                                verb="update"
                                            >
                                                <SkuTemplateAdmin />
                                            </PermissionRoute>
                                        </Route>
                                        <Route exact path="/craft-types">
                                            <Redirect to="/admin/data-dict?d=crafttype" />
                                        </Route>
                                        {/* 小贴士库 (加工小贴士条目的唯一真源) */}
                                        <Route exact path="/tips">
                                            <TipLibrary />
                                        </Route>

                                        {/* device */}
                                        <Route exact path="/devices">
                                            <DeviceList />
                                        </Route>
                                        <Route exact path="/devices/manage">
                                            <DeviceManage />
                                        </Route>
                                        {/* 设备创建向导 (2026-09-17): 「新建设备」的唯一入口 */}
                                        <Route exact path="/devices/new">
                                            <PermissionRoute
                                                resource="device_bundle"
                                                verb="create"
                                            >
                                                <DeviceCreateWizard />
                                            </PermissionRoute>
                                        </Route>
                                        <Route
                                            exact
                                            path="/devices/calibration"
                                        >
                                            <DeviceCalibration />
                                        </Route>
                                        <Route exact path="/devices/yaml">
                                            <DeviceYaml />
                                        </Route>
                                        <Route exact path="/devices/templates">
                                            <PermissionRoute
                                                resource="device_schema"
                                                verb="update"
                                            >
                                                <DeviceTemplateAdmin />
                                            </PermissionRoute>
                                        </Route>

                                        {/* parameter */}
                                        <Route exact path="/param-sets">
                                            <ParamSetList />
                                        </Route>
                                        <Route exact path="/param-sets/manage">
                                            <ParamManage />
                                        </Route>
                                        <Route
                                            exact
                                            path="/param-sets/templates"
                                        >
                                            <PermissionRoute
                                                resource="param_schema"
                                                verb="update"
                                            >
                                                <ParamTemplateAdmin />
                                            </PermissionRoute>
                                        </Route>
                                        {/* 审批中心归属管理员管理 (2026-07-30); 旧地址重定向兼容 */}
                                        <Route
                                            exact
                                            path="/param-sets/approvals"
                                        >
                                            <Redirect to="/admin/approvals" />
                                        </Route>
                                        {/* 审批中心不加 PermissionRoute: 菜单入口已按权限隐藏, 但普通用户
                                    仍要能凭工单链接查看自己提交的审批进度 (后端按提交人收紧 scope) */}
                                        <Route exact path="/admin/approvals">
                                            <ApprovalList />
                                        </Route>
                                        {/* 我的工单 (全员菜单): 同一页面的 mineOnly 模式, 只跟踪自己提交的工单 */}
                                        <Route exact path="/my-approvals">
                                            <ApprovalList mineOnly />
                                        </Route>
                                        <Route exact path="/my-permissions">
                                            <MyPermissions />
                                        </Route>

                                        {/* 数据资产 (2026-09-01): UGC 分享中心 */}
                                        <Route exact path="/assets/ugc-shares">
                                            <UgcShareList />
                                        </Route>
                                        <Route
                                            exact
                                            path="/assets/ugc-shares/:id"
                                        >
                                            <UgcShareDetail />
                                        </Route>
                                        <Route
                                            exact
                                            path="/assets/recognitions"
                                        >
                                            <RecognitionList />
                                        </Route>
                                        <Route
                                            exact
                                            path="/assets/recognitions/:id"
                                        >
                                            <RecognitionDetail />
                                        </Route>
                                        <Route
                                            exact
                                            path="/assets/matrix-recognitions"
                                        >
                                            <MatrixRecognitionList />
                                        </Route>
                                        <Route
                                            exact
                                            path="/assets/matrix-recognitions/:id"
                                        >
                                            <MatrixRecognitionDetail />
                                        </Route>
                                        <Route
                                            exact
                                            path="/assets/process-records"
                                        >
                                            <ProcessList />
                                        </Route>
                                        <Route
                                            exact
                                            path="/assets/process-records/:id"
                                        >
                                            <ProcessRecordEntry />
                                        </Route>
                                        <Route
                                            exact
                                            path="/assets/material-satisfaction"
                                        >
                                            <MaterialSatisfaction />
                                        </Route>
                                        <Route
                                            exact
                                            path="/assets/process-history"
                                        >
                                            <ProcessHistoryEntry />
                                        </Route>
                                        <Route
                                            exact
                                            path="/assets/process-history/:id"
                                        >
                                            <ProcessHistoryDetail />
                                        </Route>
                                        <Route exact path="/assets/products">
                                            <ProductList />
                                        </Route>
                                        <Route
                                            exact
                                            path="/assets/products/:asin"
                                        >
                                            <ProductDetail />
                                        </Route>
                                        {/* 商品打标人工审核 (2026-09-15): 单页审核, 结果回写 asset_product_label, 无详情子路由 */}
                                        <Route
                                            exact
                                            path="/assets/product-labels"
                                        >
                                            <ProductLabelReview />
                                        </Route>
                                        {/* 商品关联官方耗材的人工审核 (2026-10-08): 逐条裁决 + 按品类放行 */}
                                        <Route
                                            exact
                                            path="/assets/product-materials"
                                        >
                                            <ProductMaterialReview />
                                        </Route>
                                        <Route
                                            exact
                                            path="/assets/c10s-products"
                                        >
                                            <C10sList />
                                        </Route>
                                        <Route
                                            exact
                                            path="/assets/c10s-products/:id"
                                        >
                                            <C10sDetail />
                                        </Route>

                                        {/* gallery / toolbox */}
                                        <Route exact path="/gallery">
                                            <SampleList />
                                        </Route>
                                        <Route exact path="/gallery/matrix">
                                            <MatrixList />
                                        </Route>
                                        <Route
                                            exact
                                            path="/gallery/asset/:arch/:id"
                                        >
                                            <SampleDetail />
                                        </Route>
                                        <Route exact path="/tools">
                                            <PermissionRoute
                                                resource="tool"
                                                verb="update"
                                            >
                                                <ToolList />
                                            </PermissionRoute>
                                        </Route>
                                        <Route exact path="/tools/:toolKey">
                                            <ToolDetail />
                                        </Route>

                                        {/* governance */}
                                        <Route exact path="/id-mappings">
                                            <IDMappingList />
                                        </Route>
                                        <Route exact path="/id-mappings/compat">
                                            <CompatList />
                                        </Route>

                                        {/* 平台文档 (2026-10-08): 静态说明页, 所有已准入用户可见 */}
                                        <Route exact path="/docs/intro">
                                            {docPage(<PlatformIntro />)}
                                        </Route>
                                        <Route exact path="/docs/sdk">
                                            {docPage(<SdkReference />)}
                                        </Route>
                                        <Route exact path="/docs/app">
                                            {docPage(<AppApiReference />)}
                                        </Route>

                                        {/* datasync (美服→国服镜像同步) */}
                                        <Route exact path="/data-sync">
                                            <SyncOverview />
                                        </Route>
                                        <Route exact path="/data-sync/logs">
                                            <SyncLogList />
                                        </Route>

                                        {/* 平台管理页: 菜单已按权限隐藏, 这里再兜直接敲 URL 的情况 (2026-09-01 起按资源域判定) */}
                                        <Route
                                            exact
                                            path="/admin/assistant-feedback"
                                        >
                                            <PermissionRoute
                                                resource="admin_user"
                                                verb="update"
                                            >
                                                <AssistantFeedback />
                                            </PermissionRoute>
                                        </Route>
                                        <Route exact path="/admin-users">
                                            <PermissionRoute
                                                resource="admin_user"
                                                verb="update"
                                            >
                                                <UserManage />
                                            </PermissionRoute>
                                        </Route>
                                        <Route exact path="/admin/mmode">
                                            <Redirect to="/admin/data-dict?d=mmode" />
                                        </Route>
                                        {/* 2026-09-12 「Schema 管理」拆分: 参数模板→官方参数中心, 材料/设备模板→各自主数据, 字段字典→基础数据.
                                    旧地址按 query 重定向, 兼容书签与页内"引用模板"跳转 */}
                                        <Route exact path="/admin/schema">
                                            <SchemaAdminRedirect />
                                        </Route>
                                        <Route
                                            exact
                                            path="/admin/schema-templates"
                                        >
                                            <Redirect to="/param-sets/templates" />
                                        </Route>
                                        {/* 基础数据 · 字段字典 (xtool-schema SSOT 镜像): 查看人人可见, 写操作页内按 schema_field 权限判 */}
                                        <Route
                                            exact
                                            path="/admin/schema-fields"
                                        >
                                            <PermissionRoute
                                                resource="schema_field"
                                                verb="update"
                                            >
                                                <SchemaFieldAdmin />
                                            </PermissionRoute>
                                        </Route>
                                        <Route exact path="/admin/dict">
                                            <Redirect to="/admin/data-dict?d=dict" />
                                        </Route>
                                        {/* 统一「数据字典」页: 字典维护/加工模式/材料类别/工艺类型四 tab.
                                    整页不加 PermissionRoute — 材料类别/工艺类型普通用户可看, 需权限的两 tab 内部各自兜底 */}
                                        <Route exact path="/admin/data-dict">
                                            <DataDictAdmin />
                                        </Route>
                                        <Route exact path="/api-tokens">
                                            <TokenList />
                                        </Route>
                                    </Switch>
                                </RouteGuard>
                            </section>
                        </main>
                    </div>
                </AccessGate>
            </Provider>
        </Router>
    );
}

export default App;
